import React, { memo, useMemo } from "react";
import { OnboardingProvider, withOnboardingProvider } from "@/Onboarding";
import { useSelector } from "react-redux";
import { GlobalState } from "@/Reducers/interface";
import { Stages } from "@/Onboarding";
import { CurrentGuideProgress } from "@/Onboarding/store/reducers";
import { useHistory, useRouteMatch } from "react-router-dom";
import { ZomentumRoutes } from "@/Contracts";
import { useFeatureFlag } from "@/Hooks/useFeature";

const OnboardingWrapper: React.FC = ({ children }) => {
	const history = useHistory();
	const { configurationSettings, userData, userCompany } = useSelector(
		(state: GlobalState) => ({
			configurationSettings: state.user.configurationSettings,
			userData: state.user.userData,
			userCompany: state.user.userCompany
		})
	);
	const isOnboardingEnabled = useFeatureFlag("in_app_onboarding");
	const featureFlag = useMemo(
		() => ({
			isOnboardingEnabled
		}),
		[isOnboardingEnabled]
	);
	const signUpInitiatingVendor =
		configurationSettings?.signup_initiating_vendor || "";
	const config = useMemo(
		() => ({
			signUpInitiatingVendor,
			userData,
			userCompany,
			skipOnboarding: configurationSettings?.skip_onboarding
		}),
		[
			userData,
			signUpInitiatingVendor,
			userCompany,
			configurationSettings?.skip_onboarding
		]
	);

	const stageURLMap: Record<Stages, ZomentumRoutes> = {
		[Stages.DOCUMENT_INTRODUCTION]: ZomentumRoutes.DocumentsList,
		[Stages.ADD_DOCUMENT]: ZomentumRoutes.DocumentsList,
		[Stages.SELECTING_TEMPLATE]: ZomentumRoutes.DocumentsList,
		[Stages.EXPLORE_EDITOR]: ZomentumRoutes.DocumentsDetail,
		[Stages.ALIGN_INTRODUCTION]: ZomentumRoutes.Marketplace,
		[Stages.ALIGN_VENDORS_AND_RESOURCES]: ZomentumRoutes.Marketplace,
		[Stages.ALIGN_EXPLORE_RESOURCES]: ZomentumRoutes.Marketplace,
		[Stages.OPPORTUNITY_INTRODUCTION]: ZomentumRoutes.OpportunityList,
		[Stages.ADD_OPPORTUNITY]: ZomentumRoutes.OpportunityList,
		[Stages.OPPORTUNITY_ACTIVITIES]: ZomentumRoutes.OpportunityDetail,
		[Stages.INTEGRATION_SELECT_APP]: ZomentumRoutes.IntegrationSettings,
		[Stages.INTEGRATION_INTRODUCTION]: ZomentumRoutes.IntegrationSettings,
		[Stages.INTEGRATION_APPS]: ZomentumRoutes.IntegrationSettings
	};

	const isDocumentDetailPage = useRouteMatch(
		ZomentumRoutes.DocumentsDetail
	)?.isExact;
	const isDocumentPreviewPage = useRouteMatch(
		ZomentumRoutes.DocumentsPreview
	)?.isExact;
	const isDocumentPDFPage = useRouteMatch(
		ZomentumRoutes.DocumentsPdfView
	)?.isExact;
	const isNotDocumentPage = !(
		isDocumentDetailPage ||
		isDocumentPreviewPage ||
		isDocumentPDFPage
	);

	const handleRedirect = (
		progress: CurrentGuideProgress,
		entityId: string
	) => {
		if (progress.activeStage && isNotDocumentPage) {
			const url = stageURLMap[progress.activeStage];
			if (url) {
				history.push(url.replace(":id", entityId));
			}
		}
	};

	const props = {
		featureFlag,
		config,
		handles: {
			handleRedirect
		}
	};
	return <OnboardingProvider {...props}>{children}</OnboardingProvider>;
};

export default withOnboardingProvider(memo(OnboardingWrapper));
